import { useState, useEffect, useRef } from 'react';
import { playCardSnap } from '../../utilities/sounds';
import ChipStack from './ChipStack';
import { hashStr, formatAmount } from '../../utilities/chips';
import { CardModBadges, OriginalCardFace, cardModsFor } from './CardMods';
import { cardIdAt } from '../../redux/pupSlice';
import './CommunityCards.css';

const suitSymbols = { h: '♥', d: '♦', c: '♣', s: '♠' };
const suitColors  = { h: '#f0245e', d: '#f0245e', c: '#191e4e', s: '#191e4e' };

const DEAL_INTERVAL_MS = 350;
// Press-and-hold a changed board card this long to peek its original face (mirrors HoleCards).
const HOLD_ORIGINAL_MS = 160;

// mods: the value changes a Power-Up made to THIS board card this round (Substitution / a
// board-targeted rank/suit shift) — drives the public "modified" badge(s) + hold-to-peek-original.
const CardDisplay = ({ card, animating, winner = false, wild = false, targetable = false, onClick = null, style, mods = null, holdingOriginal = false, onHoldStart = null, onHoldEnd = null }) => {
    const rank = card.slice(0, -1);
    const suit = card.slice(-1);
    return (
        <div
            className={`card card-face${animating ? ' card-dealing' : ''}${winner ? ' card-winner' : ''}${wild ? ' card-wild' : ''}${targetable ? ' card-targetable' : ''}`}
            style={{ color: suitColors[suit] || '#191e4e', ...style }}
            onClick={onClick || undefined}
            onPointerDown={onHoldStart || undefined}
            onPointerUp={onHoldEnd || undefined}
            onPointerLeave={onHoldEnd || undefined}
            onPointerCancel={onHoldEnd || undefined}
        >
            <div className="card-rank">{rank}</div>
            <div className="card-suit">{suitSymbols[suit] || suit}</div>
            <div className="card-rank-big">{rank}</div>
            {wild && <div className="card-wild-badge">WILD</div>}
            {holdingOriginal && mods && mods.length > 0 && <OriginalCardFace token={mods[0].original} />}
            {mods && mods.length > 0 && <CardModBadges mods={mods} />}
        </div>
    );
};

// highlightIndexes: a Set of board POSITIONS to gold-border — the community cards that are part
// of a winner's best hand at showdown. null/empty during normal play = no board highlight.
// wildIndexes: a Set of board POSITIONS a Power-Up turned WILD this round (badge + glow), from
// the authoritative game payload. Positions, not tokens: two cards can legitimately hold the same
// token (Suit Change / Super Suit rewrite one card into another's value), so matching by value
// would badge a card that is not wild.
// cardIds: the board's card identities, positionally aligned with `cards` — the key a card's
// provenance is stored under, so its badges follow the card rather than the slot.
// blindPot: the round's Flying Blind side pot (non-blind bet/call/raise surcharges) — shown as
// its own line under the pot so the taxed chips visibly land somewhere; 0 = no line.
// cardTargetActive/onCardTarget: while the hero's own card-TARGETING Power-Up whose scope reaches
// the board (Shrinkify's ANY, or a COMMUNITY / COMMUNITY_OR_OWN_HOLE card) waits on its pick, every
// board card pulses (.card-targetable) and a click calls onCardTarget(index) with the card's
// 0-based board position — the same one-shot-guard + toast machinery HoleCards uses.
// mods: { <cardId>: [{ by, original, current, code }, ...] } — value changes a Power-Up made to
// board cards this round (a board-targeted rank/suit shift). Public: badges + hold-to-see-original
// render for everyone. Keyed by card IDENTITY, so a Substitution shifting the board cannot move
// one card's history onto another.
const CommunityCards = ({ cards, cardIds = null, pot, pots, blindPot = 0, chipScale = 1, highlightIndexes = null, wildIndexes = null, cardTargetActive = false, onCardTarget = null, mods = null }) => {
    // Side-pot breakdown (Main pot / Side pot N) is only sent while a non-folded player is
    // all-in; otherwise show the single combined pot.
    const hasSplit = Array.isArray(pots) && pots.length > 1;
    const incoming = cards ? cards.trim().split(/\s+/).filter(Boolean) : [];
    // Seed from the cards already on the board so re-entering a table mid-hand (in-game →
    // lobby → back) shows them immediately. Only increases that happen while mounted (a new
    // flop/turn/river — i.e. a real stage transition) animate; the initial mount never
    // re-deals the community cards.
    const [visibleCount, setVisibleCount] = useState(() => incoming.length);
    const [animatingIndexes, setAnimatingIndexes] = useState(new Set());
    const prevCountRef = useRef(incoming.length);
    // Failure toast + one-at-a-time guard for a board card-target pick (mirrors HoleCards).
    const [targetError, setTargetError] = useState(null);
    const targetPickRef = useRef(false);
    // Press-and-hold a changed board card to peek its original face. Only arms when the card has
    // changes and no board pick window is live (a pick click must not become a hold).
    const [heldOriginalIndex, setHeldOriginalIndex] = useState(null);
    const holdTimerRef = useRef(null);
    useEffect(() => () => { if (holdTimerRef.current) clearTimeout(holdTimerRef.current); }, []);
    const startHoldOriginal = (e, index, hasMods) => {
        if (!hasMods || cardTargetActive) return;
        if (e.pointerType === 'mouse' && e.button !== 0) return; // left button only
        try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) { /* older browsers */ }
        if (holdTimerRef.current) clearTimeout(holdTimerRef.current);
        holdTimerRef.current = setTimeout(() => {
            holdTimerRef.current = null;
            setHeldOriginalIndex(index);
        }, HOLD_ORIGINAL_MS);
    };
    const endHoldOriginal = () => {
        if (holdTimerRef.current) { clearTimeout(holdTimerRef.current); holdTimerRef.current = null; }
        setHeldOriginalIndex((cur) => (cur == null ? cur : null));
    };

    const pickCard = (index) => {
        if (!cardTargetActive || !onCardTarget || targetPickRef.current) return;
        targetPickRef.current = true;
        Promise.resolve(onCardTarget(index))
            .catch((e) => {
                setTargetError(typeof e === 'string' && e ? e : 'Could not pick that card');
                setTimeout(() => setTargetError(null), 4000);
            })
            .finally(() => { targetPickRef.current = false; });
    };

    useEffect(() => {
        const prev = prevCountRef.current;
        const next = incoming.length;

        if (next === 0) {
            setVisibleCount(0);
            setAnimatingIndexes(new Set());
            prevCountRef.current = 0;
            return;
        }

        if (next <= prev) {
            // Cards were reset (new hand) — show immediately without animation
            setVisibleCount(next);
            setAnimatingIndexes(new Set());
            prevCountRef.current = next;
            return;
        }

        // New cards arrived — reveal one at a time
        const newCards = next - prev;
        for (let i = 0; i < newCards; i++) {
            const idx = prev + i;
            setTimeout(() => {
                setVisibleCount(idx + 1);
                setAnimatingIndexes(s => new Set(s).add(idx));
                playCardSnap();
                // Remove animation class after it finishes
                setTimeout(() => {
                    setAnimatingIndexes(s => {
                        const copy = new Set(s);
                        copy.delete(idx);
                        return copy;
                    });
                }, 350);
            }, i * DEAL_INTERVAL_MS);
        }
        prevCountRef.current = next;
    }, [incoming.length]); // eslint-disable-line react-hooks/exhaustive-deps

    // Render only the cards that have actually been dealt/revealed — no placeholder slots,
    // so the board shows nothing until the flop drops, then grows as cards arrive.
    const slots = incoming.slice(0, visibleCount);

    // A normal board is 5 cards, but the "extra community card" Power-Up can push it to 6 or 7
    // (or more). Keep the row within the SAME footprint a 5-card board already occupies by
    // overlapping the cards just enough — so a fatter board can never overflow the felt, whether
    // single-table or the narrower split-screen. ≤5 cards render exactly as before (overlap 0).
    // The values below MUST match CommunityCards.css (.card width 48px, .cards-row gap 8px).
    const CARD_W = 48, GAP = 8;
    const MAX_ROW_WIDTH = CARD_W * 5 + GAP * 4; // the classic 5-card footprint (272px)
    const n = slots.length;
    const naturalWidth = n * CARD_W + Math.max(0, n - 1) * GAP;
    // Overlap per card (applied as a negative left margin to every card after the first). Capped
    // so an extreme board still shows a readable left strip of each card (the rank+suit index).
    const overlap = n > 1 && naturalWidth > MAX_ROW_WIDTH
        ? Math.min(32, (naturalWidth - MAX_ROW_WIDTH) / (n - 1))
        : 0;

    return (
        <div className="community-cards">
            {targetError && <div className="discard-error">{targetError}</div>}
            <div className="cards-row">
                {slots.map((card, i) => {
                    const cardMods = cardModsFor(mods, cardIdAt(cardIds, i));
                    return (
                        <CardDisplay
                            key={i}
                            card={card}
                            animating={animatingIndexes.has(i)}
                            winner={!!highlightIndexes?.has(i)}
                            wild={!!wildIndexes?.has(i)}
                            targetable={cardTargetActive}
                            onClick={cardTargetActive ? () => pickCard(i) : undefined}
                            style={i > 0 && overlap > 0 ? { marginLeft: `-${overlap}px` } : undefined}
                            mods={cardMods}
                            holdingOriginal={heldOriginalIndex === i}
                            onHoldStart={(e) => startHoldOriginal(e, i, !!(cardMods && cardMods.length))}
                            onHoldEnd={endHoldOriginal}
                        />
                    );
                })}
            </div>
            {(pot > 0 || blindPot > 0) && (
                <div className="pot-area">
                    {pot > 0 && (
                        <ChipStack amount={pot} seed={hashStr(`pot:${pot}`)} scale={chipScale} label={false} />
                    )}
                    {hasSplit ? (
                        <div className="pot-breakdown">
                            {pots.map((p, i) => (
                                <div key={i} className="pot-display">{p.label}: {formatAmount(p.amount)}</div>
                            ))}
                        </div>
                    ) : (
                        pot > 0 && <div className="pot-display">Pot: {formatAmount(pot)}</div>
                    )}
                    {blindPot > 0 && (
                        <div className="pot-display">Blind pot: {formatAmount(blindPot)}</div>
                    )}
                </div>
            )}
        </div>
    );
};

export default CommunityCards;
